import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { microsoftService } from '../services';
import { getErrorMessage } from '../utils/errors';

const MicrosoftCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam);
        setIsProcessing(false);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setIsProcessing(false);
        return;
      }

      try {
        // Get the PKCE code verifier and OAuth params from session storage
        const codeVerifier = sessionStorage.getItem('microsoft_code_verifier');
        const clientId = sessionStorage.getItem('microsoft_client_id');
        const redirectUri = sessionStorage.getItem('microsoft_redirect_uri');
        const scope = sessionStorage.getItem('microsoft_scope');

        // Clean up session storage
        sessionStorage.removeItem('microsoft_code_verifier');
        sessionStorage.removeItem('microsoft_client_id');
        sessionStorage.removeItem('microsoft_redirect_uri');
        sessionStorage.removeItem('microsoft_scope');

        if (!codeVerifier || !clientId || !redirectUri || !scope) {
          setError('Missing OAuth parameters. Please try connecting again.');
          setIsProcessing(false);
          return;
        }

        // Exchange the code for tokens directly with Microsoft (SPA flow)
        const tokens = await microsoftService.exchangeTokenWithMicrosoft(
          code,
          codeVerifier,
          clientId,
          redirectUri,
          scope,
        );

        // Store the tokens in our backend
        await microsoftService.storeTokens({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          expiresIn: tokens.expires_in,
          scope: tokens.scope,
        });

        // Redirect back to the original page or dashboard
        const returnUrl = sessionStorage.getItem('microsoft_auth_return');
        sessionStorage.removeItem('microsoft_auth_return');

        if (returnUrl) {
          window.location.href = returnUrl;
        } else {
          navigate('/dashboard');
        }
      } catch (err: unknown) {
        setError(getErrorMessage(err, 'Failed to connect Microsoft account'));
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (isProcessing) {
    return (
      <div className="h-full flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Connecting Microsoft Account</h2>
          <p className="text-neutral-600">Please wait while we complete the authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-neutral-50">
        <div className="max-w-md w-full bg-surface rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-danger-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Connection Failed</h2>
          <p className="text-neutral-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MicrosoftCallback;
